// // pages/activities/internships/index.tsx

import {
  createInternshipColumns,
  InternshipActionType,
} from "@/colDef/internshipColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import InternshipDialog from "@/components/formDialog/InternshipDialog";
import Search from "@/components/Search";
import { ActionDropdown, ActionDropdownContent, ActionDropdownTrigger, DropdownNode } from "@/components/ActionDropdown";
import {

  useDeleteInternshipMutation,
  useInternshipsQuery,
} from "@/hooks/api/internship.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";

import {
  InternshipParamsType,
  InternshipType,
} from "@/services/internship.service";
import { Plus, Filter } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useUrlFilters from "@/hooks/useUrlFilters";

import {
  usePublishInternshipMutation,
  useCloseInternshipMutation,
} from "@/hooks/api/internship.queries";

const InternshipsPage = () => {
  const navigate = useNavigate();

  const {
    page,
    limit,
    search: searchTerm,
    getFilter,
    setPage,
    setSearch,
    setFilter,
  } = useUrlFilters({ defaultLimit: 10 });

  const statusFilter = getFilter("status") || "all";
  const typeFilter = getFilter("type") || "all";

  // Still debounce for the API call to avoid too many requests
  const debouncedSearch = useDebounce(searchTerm, 300);

  const deleteDialog = useAlertDialog();
  const internshipDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    internshipId: "",
    internshipData: null as InternshipType | null,
  });


  const filters: InternshipParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter && statusFilter !== "all" && { status: statusFilter }),
      ...(typeFilter && typeFilter !== "all" && { type: typeFilter }),
    }),
    [debouncedSearch, statusFilter, typeFilter, page, limit]
  );

  const handleSetStatus = (val: string) => {
    setFilter("status", val === "all" ? "" : val);
  };

  const statusDropdownShape: DropdownNode[] = [
    { type: "item", label: "All", onSelect: () => handleSetStatus("all") },
    {
      type: "item",
      label: "Open",
      onSelect: () => handleSetStatus("open"),
    },
    {
      type: "item",
      label: "Close",
      onSelect: () => handleSetStatus("closed"),
    },
  ];

  const triggerLabel =
    !statusFilter || statusFilter === "all"
      ? "Status: All"
      : `Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`;

  const handleSetType = (val: string) => {
    setFilter("type", val === "all" ? "" : val);
  };

  const typeDropdownShape: DropdownNode[] = [
    { type: "item", label: "All Types", onSelect: () => handleSetType("all") },
    { type: "item", label: "Internship", onSelect: () => handleSetType("internship") },
    { type: "item", label: "Full-Time", onSelect: () => handleSetType("full-time") },
    { type: "item", label: "Part-time", onSelect: () => handleSetType("part-time") },
  ];

  const typeTriggerLabel =
    !typeFilter || typeFilter === "all"
      ? "Type: All"
      : `Type: ${typeFilter === "full-time" ? "Full-Time" : typeFilter === "part-time" ? "Part-time" : "Internship"}`;


  const publishMutation = usePublishInternshipMutation(filters);
  const closeMutation = useCloseInternshipMutation(filters);


  const { data, isLoading, isError, refetch } =
    useInternshipsQuery(filters);


  const deleteMutation = useDeleteInternshipMutation(filters);

  const rawInternships = data?.internships ?? [];

  // Client-side filtering — instant on every keypress, regardless of backend support
  const internships = useMemo(() => {
    let result = rawInternships;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (i: InternshipType) =>
          i.title?.toLowerCase().includes(q) ||
          i.domain?.toLowerCase().includes(q) ||
          i.type?.toLowerCase().includes(q) ||
          i.location?.toLowerCase().includes(q)
      );
    }
    if (statusFilter && statusFilter !== "all") {
      result = result.filter(
        (i: InternshipType) => i.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    if (typeFilter && typeFilter !== "all") {
      result = result.filter(
        (i: InternshipType) => i.type?.toLowerCase() === typeFilter.toLowerCase()
      );
    }
    return result;
  }, [rawInternships, searchTerm, statusFilter, typeFilter]);

  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── DELETE ─────────────────────────
  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  // ─── ACTION CLICK ───────────────────
  const handleActionClick = useCallback(
    (payload: InternshipActionType) => {

      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Delete internship <strong>{payload.name}</strong>?
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      }

      else if (payload.action === "view") {
        navigate(`/activities/internships/${payload.id}`);
      }

      else if (payload.action === "edit") {
        const internshipData =
          internships.find((i: InternshipType) => i.id === payload.id) ?? null;

        internshipDialog.open({
          mode: "edit",
          internshipId: payload.id,
          internshipData,
        });
      }

      else if (payload.action === "publish") {
        publishMutation.mutate(payload.id);
      }

      else if (payload.action === "close") {
        closeMutation.mutate(payload.id);
      }

    },
    [deleteDialog, internshipDialog, internships, publishMutation, closeMutation]
  );

  const columns = useMemo(
    () => createInternshipColumns(handleActionClick),
    [handleActionClick]
  );



  // ─── ERROR ─────────────────────────
  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load internships."
        onRetry={refetch}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          Internship Management
        </h1>

        <Button
          onClick={() =>
            internshipDialog.open({
              mode: "create",
              internshipId: "",
              internshipData: null,
            })
          }
          className="flex items-center gap-2 bg-gray-900 text-white"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search internships..."
          value={searchTerm}
          onValueChange={setSearch}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <ActionDropdown menuModal={false}>
          <ActionDropdownTrigger
            triggerContent={
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-500" />
                <span className="text-sm">{typeTriggerLabel}</span>
              </div>
            }
            buttonVariant="outline"
            buttonSize="default"
            className={{
              buttonClass: "h-9 bg-white font-normal text-gray-700",
            }}
          />
          <ActionDropdownContent
            align="start"
            dropdownShape={typeDropdownShape}
            className="w-40"
          />
        </ActionDropdown>

        <ActionDropdown menuModal={false}>
          <ActionDropdownTrigger
            triggerContent={
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-500" />
                <span className="text-sm">{triggerLabel}</span>
              </div>
            }
            buttonVariant="outline"
            buttonSize="default"
            className={{
              buttonClass: "h-9 bg-white font-normal text-gray-700",
            }}
          />
          <ActionDropdownContent
            align="start"
            dropdownShape={statusDropdownShape}
            className="w-40"
          />
        </ActionDropdown>
      </div>

      {/* Count */}
      {!isLoading && (
        <p className="text-sm text-blue-600">
          Showing {internships.length} of {totalEntries}
        </p>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<InternshipType, any>
              columns={columns}
              data={internships}
              onRowClick={(row) =>
                navigate(`/activities/internships/${row.id}`)
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={internships.length}
        onPageChange={setPage}
      />

      {/* Dialog */}
      <InternshipDialog
        mode={internshipDialog.state.mode}
        isOpen={internshipDialog.state.isOpen}
        onOpenChange={(open) => !open && internshipDialog.close()}
        filters={filters}
        internshipId={internshipDialog.state.internshipId}
        internshipData={internshipDialog.state.internshipData}
        onCancel={internshipDialog.close}
      />

      {/* Delete Dialog */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default InternshipsPage;











